import React, { useState, useEffect } from "react";
import { Chart } from "primereact/chart";
import { IDashboardData, Run } from "../../interfaces/IDashboardData";
import { getDashboardData } from "../../services/DashboardService";
import { Row, Col } from 'react-bootstrap'; 
import "./Dashboard.css";
import { useNavigate } from "react-router-dom";
import { calculateItemsPerPage } from "../../helpers/CalculateItemsPerPage";
import { RunsResponse } from "../../interfaces/ILogs";

const Dashboard: React.FC = () => {
  const navigate = useNavigate();

  const [jobData, setJobData] = useState<IDashboardData | null>(null);
  const [filesChartData, setFilesChartData] = useState<any>({});
  const [tasksChartData, setTasksChartData] = useState<any>({});
  const [chartOptions, setChartOptions] = useState<any>({});
  const [initialLoad, setInitialLoad] = useState(false);

  useEffect(() => {
    setInitialLoad(true);
    async function fetchData() {
      const data = await getDashboardData();
      setJobData(data);
      updateCharts(data.TodaysRuns);
    }
    calculateItemsPerPage();
    fetchData();
  }, []); 
  
  // Load the runs in background after jobData is successfully fetched
  useEffect(() => {
    if (jobData && initialLoad) { 
      setInitialLoad(false);
    }
  }, [jobData]);  
 
  const updateCharts = (runs: Run[]) => {
    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue("--text-color");
    const textColorSecondary = documentStyle.getPropertyValue("--text-color-secondary");
    const surfaceBorder = documentStyle.getPropertyValue("--surface-border");

    const groupBy = (array: Run[], key: keyof Run) => {
      return array.reduce((result, run) => {
        const group = run[key] as string;

        // Skip empty MessageTypeCode or 'SFTP' specifically for filesData
        if (key === "MessageTypeCode" && (group === "" || group === "SFTP")) {
          return result;
        }

        if (!result[group]) {
          result[group] = { success: 0, fail: 0, successIds: [], failIds: [] };
        }
        if (run.StatusSucceeded) {
          result[group].success++;
          result[group].successIds.push(run.RunId);
        } else {
          result[group].fail++;
          result[group].failIds.push(run.RunId);
        }
        return result;
      }, {} as Record<string, { success: number; fail: number; successIds: string[]; failIds: string[] }>);
    };

    const filesData = groupBy(runs, "MessageTypeCode");
    const tasksData = groupBy(runs, "Task");

    const createChartData = (data: Record<string, { success: number; fail: number; successIds: string[]; failIds: string[] }>) => ({
      labels: Object.keys(data),
      datasets: [
        {
          type: "bar",
          label: "Succeeded",
          backgroundColor: "#4CD07D",
          data: Object.values(data).map((item) => item.success),
          runIds: Object.values(data).map((item) => item.successIds), // Directly use the successIds
        },
        {
          type: "bar",
          label: "Failed",
          backgroundColor: "#FF6259",
          data: Object.values(data).map((item) => item.fail),
          runIds: Object.values(data).map((item) => item.failIds), // Directly use the failIds
        },
      ],
    });

    setFilesChartData(createChartData(filesData));
    setTasksChartData(createChartData(tasksData));



    setChartOptions({
      maintainAspectRatio: false,
      aspectRatio: 0.8,
      plugins: {
        legend: {
          labels: {
            color: textColor,
          },
        },
      },
      scales: {
        x: {
          stacked: true,
          ticks: {
            color: textColorSecondary,
          },
          grid: {
            color: surfaceBorder,
          },
        },
        y: {
          stacked: true,
          ticks: {
            color: textColorSecondary,
          },
          grid: {
            color: surfaceBorder,
          },

        },
      },
    });
  };

  type ChartClickContext = "Files" | "Tasks";

  const handleChartClick = (
    event: any,
    elements: any,
    chartData: any,
    context: ChartClickContext
  ) => {
    if (!elements.length) return;
  
    const { datasetIndex, index } = elements[0];
    const selectedData = chartData.datasets[datasetIndex];
    const selectedKey = chartData.labels[index];
  
    let filter: Record<string, string> = {};
  
    // Set the correct filter based on the chart clicked
    if (context === "Files") {
      filter.Files = selectedKey; // For the files chart
    } else if (context === "Tasks") {
      filter.Tasks = selectedKey; // For the tasks chart
    }
  
    // Set the status filter based on whether the clicked bar is 'Succeeded' or 'Failed'
    filter.Status = selectedData.label === "Succeeded" ? "Success" : "Failed";
  
    // Set TimeRange to "CurrentDay" as the dashboard only displays today's data
    filter.TimeRange = "CurrentDay";
  
    // Pass the filter to the Logs page
    navigate("/logs", { state: { filter } });
  };
  

  if (!jobData) {
    return <p>Loading...</p>;
  }

  return (
    <div>
      <div className="page-title">
        <p>Task Performance Dashboard</p>
        <p className="dashboard-last-updated">Last Updated: {new Date().toLocaleString()}</p>
      </div>

      <div className="grid-container">
        <div className="metric-card">
          <h3 className="metric-title">Calculated Usage Today</h3>
          <p className="metric-value" style={{ color: "#28a745" }}>
            {jobData.DashboardSummary.CalculatedUsageToday}
          </p>
        </div>
        <div className="metric-card">
          <h3 className="metric-title">Estimated Usage Today</h3>
          <p className="metric-value" style={{ color: "#28a745" }}>
            {jobData.DashboardSummary.EstimatedUsageToday}
          </p>
        </div>
        <div className="metric-card">
          <h3 className="metric-title">Failed Validations</h3>
          <p className="metric-value" style={{ color: "#ffc107" }}>
            {jobData.DashboardSummary.FailedValidationsToday}
          </p>
        </div>
        <div className="metric-card">
          <h3 className="metric-title">Errors Today</h3>
          <p className="metric-value" style={{ color: "#dc3545" }}>
            {jobData.DashboardSummary.ErrorsToday}
          </p>
        </div>
      </div>

      <Row>
        <Col xs={12} md={12} className="chart-col">
          <div className="chart-card">
            <h3 className="metric-title">Files</h3>
            <Chart type="bar" data={filesChartData}
              options={{
                ...chartOptions,
                onClick: (event: any, elements: any) => handleChartClick(event, elements, filesChartData, "Files"),
              }}
            />
          </div>
        </Col>
        <Col xs={12} md={12} className="chart-col">
          <div className="chart-card">
            <h3 className="metric-title">Tasks</h3>
            <Chart type="bar"
              data={tasksChartData}
              options={{
                ...chartOptions,
                onClick: (event: any, elements: any) => handleChartClick(event, elements, tasksChartData,  "Tasks"),
              }} />
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
